// ---------------------------------------------------------------------------
// Shortlists API client
// ---------------------------------------------------------------------------
// These endpoints use plain fetch with credentials: "include" (cookie auth)
// instead of the typed apiClient (ApiHttpMethod missing delete/patch).

const API_BASE =
  typeof window === "undefined"
    ? process.env.ALISTER_API_URL ?? "http://127.0.0.1:8000"
    : "";

// -- Response types ----------------------------------------------------------

export interface ShortlistSummary {
  id: number;
  name: string;
  description: string | null;
  candidate_count: number;
  rating_summary: Record<string, number>;
  updated_at: string | null;
}

export interface ShortlistListResponse {
  shortlists: ShortlistSummary[];
}

export interface ShortlistItemDetail {
  id: number;
  person_entity_id: number;
  name: string | null;
  headline: string | null;
  location: string | null;
  overview: string | null;
  keywords: string[];
  quality_score: number | null;
  social_links: Record<string, string | null>;
  rating: string | null;
  notes: string | null;
  added_at: string | null;
}

export interface ShortlistDetailResponse {
  id: number;
  name: string;
  description: string | null;
  status: string;
  candidate_count: number;
  items: ShortlistItemDetail[];
  created_at: string | null;
  updated_at: string | null;
}

export interface AddItemResponse {
  id: number;
  shortlist_id: number;
  person_entity_id: number;
  added_at: string | null;
}

export interface MembershipResponse {
  membership: Record<number, number[]>;
}

// -- Helpers -----------------------------------------------------------------

function handle401(): never {
  if (typeof window !== "undefined") {
    window.location.href = `/login?next=${encodeURIComponent(window.location.pathname)}`;
  }
  // Return a never-resolving promise so callers don't continue
  return new Promise<never>(() => {}) as never;
}

async function jsonFetch<T>(
  url: string,
  init?: RequestInit
): Promise<T> {
  const response = await fetch(url, {
    credentials: "include",
    ...init,
  });

  if (response.status === 401) {
    return handle401();
  }

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as unknown as T;
  }

  return response.json();
}

// -- Linked-entity errors ----------------------------------------------------

export class LinkedConversationError extends Error {
  constructor(
    public linkedConversationId: number,
    public linkedConversationTitle: string | null,
  ) {
    super("Shortlist has a linked conversation");
  }
}

// -- API functions -----------------------------------------------------------

export async function listShortlists(): Promise<ShortlistListResponse> {
  return jsonFetch<ShortlistListResponse>(
    `${API_BASE}/api/v1/shortlists`
  );
}

export async function createShortlist(
  name: string
): Promise<ShortlistSummary> {
  return jsonFetch<ShortlistSummary>(
    `${API_BASE}/api/v1/shortlists`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    }
  );
}

export async function deleteShortlist(
  id: number,
  opts?: { alsoDeleteConversation?: boolean; unlinkConversation?: boolean },
): Promise<void> {
  const params = new URLSearchParams();
  if (opts?.alsoDeleteConversation) params.set("also_delete_conversation", "true");
  if (opts?.unlinkConversation) params.set("unlink_conversation", "true");
  const qs = params.toString();
  const response = await fetch(`${API_BASE}/api/v1/shortlists/${id}${qs ? `?${qs}` : ""}`, {
    method: "DELETE",
    credentials: "include",
  });

  if (response.status === 401) return handle401();

  if (response.status === 409) {
    const body = await response.json();
    if (body?.detail?.code === "linked_conversation") {
      throw new LinkedConversationError(
        body.detail.linked_conversation_id,
        body.detail.linked_conversation_title,
      );
    }
    throw new Error("Conflict");
  }

  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
}

export async function deleteAllShortlists(): Promise<void> {
  return jsonFetch<void>(
    `${API_BASE}/api/v1/shortlists`,
    { method: "DELETE" }
  );
}

export async function renameShortlist(
  id: number,
  name: string
): Promise<ShortlistSummary> {
  return jsonFetch<ShortlistSummary>(
    `${API_BASE}/api/v1/shortlists/${id}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    }
  );
}

export async function getShortlist(
  id: number,
  sort?: string,
  q?: string
): Promise<ShortlistDetailResponse> {
  const params = new URLSearchParams();
  if (sort) params.set("sort", sort);
  if (q) params.set("q", q);
  const qs = params.toString();
  return jsonFetch<ShortlistDetailResponse>(
    `${API_BASE}/api/v1/shortlists/${id}${qs ? `?${qs}` : ""}`
  );
}

export async function addItem(
  shortlistId: number,
  personEntityId: number
): Promise<AddItemResponse> {
  return jsonFetch<AddItemResponse>(
    `${API_BASE}/api/v1/shortlists/${shortlistId}/items`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ person_entity_id: personEntityId }),
    }
  );
}

export async function removeItem(
  shortlistId: number,
  itemId: number
): Promise<void> {
  return jsonFetch<void>(
    `${API_BASE}/api/v1/shortlists/${shortlistId}/items/${itemId}`,
    { method: "DELETE" }
  );
}

export async function updateItem(
  shortlistId: number,
  itemId: number,
  data: { rating?: string | null; notes?: string | null }
): Promise<void> {
  return jsonFetch<void>(
    `${API_BASE}/api/v1/shortlists/${shortlistId}/items/${itemId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }
  );
}

export async function getMembership(
  personEntityIds: number[]
): Promise<MembershipResponse> {
  if (personEntityIds.length === 0) {
    return { membership: {} };
  }
  const ids = personEntityIds.join(",");
  return jsonFetch<MembershipResponse>(
    `${API_BASE}/api/v1/shortlists/membership?person_entity_ids=${ids}`
  );
}
